/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Abilities from './model/Abilities';
import Monster from './model/Monster';

interface MarkdownTextProps {
  text: string;
  mon: Monster | null;
}

const MarkdownText: React.FC<MarkdownTextProps> = ({ text, mon }) => {
  const processText = (sourceText: string): string[] => {
    const sections: string[] = [];
    let currentSection: string[] = [];

    sourceText?.split("\n").forEach((line) => {
      const trimmedLine = line.trim();
      const isGrouping = trimmedLine.startsWith('|') || trimmedLine.startsWith('* ') || trimmedLine.startsWith('- ');

      if (isGrouping) {
        // Group all the lines that make up a table, list, or indent block into a single section:
        currentSection.push(line); // Append line to current section
      } else {
        // Start new section
        if (currentSection.length) {
          sections.push(currentSection.join('\n'));
          currentSection = [];
        }
        sections.push(line);
      }
    });
    if (currentSection.length) {
      sections.push(currentSection.join('\n'));
    }
    return sections;
  };

  // Sanitize markdown input to prevent XSS attacks
  const sanitizeMarkdownInput = (input: string): string => {
    // Basic sanitization: Remove script and iframe tags
    return input.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
                .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '');
  };

  const sections = processText(text);
  const content = sections.map((line, index) => {
    const sanitizedLine = sanitizeMarkdownInput(line);
    const markdown = (
      <ReactMarkdown
        key={`md-${index}`}
        children={mon ? Abilities.ReplaceTags(sanitizedLine, mon) : sanitizedLine}
        remarkPlugins={[remarkGfm]}
        // Disable dangerous HTML elements
        disallowedElements={['script', 'iframe', 'img']}
        unwrapDisallowed={true}
        components={{
          p: ({ node, ...props }) => <span key={`span-${index}`} {...props} />, // Convert <p> tags to <span> to prevent nesting issues
          table: ({ node, ...props }) => <table className='markdown-table' {...props} />,
          a: ({node, ...props}) => <a target="_blank" rel="noopener noreferrer" {...props} />
        }}
      />
    );

    if (index === 0) {
      // We want the first line to be a <span> so it shows on the same line as the header
      return <span key={`span1-${index}`}>{markdown}</span>
    }

    // But anything after the first line needs to be on its own line, so we use <section> elements
    return <section key={`section-${index}`}>{markdown}</section>
  });

  return <span className='markdown'>{content}</span>;
}

export default MarkdownText;